import { apiSlice } from './apiSlice'

export interface Evidence {
  id: string
  case_id: string
  team_id: string
  title: string
  description?: string
  file_url: string
  file_name: string
  file_size: number
  mime_type: string
  category?: string
  tags: string[]
  uploaded_at?: string
  uploaded_by: string
  storage_path?: string
  is_public?: boolean
  public_at?: string | null
  public_by?: string | null
}

export interface PublicizeCaseRequest {
  case_id: string
  team_ids?: string[]
}

export interface EvidencePoolItem {
  document_id: string
  case_id: string
  title: string
  description?: string
  file_name: string
  file_size: number
  mime_type: string
  document_type: string
}

export interface EvidenceAiSuggestionsResponse {
  case_id: string
  suggestions: { id: string; title: string; detail: string; source_document_id?: string }[]
  notice: string
}

/** Đồng bộ với GET /evidence/deadline-status */
export interface EvidenceDeadlineStatus {
  case_id: string
  submission_open: boolean
  message: string
  case_deadline?: string | null
  round_evidence_deadline?: string | null
  round_id?: string
  round_opens_at?: string | null
  round_closes_at?: string | null
  server_now_iso?: string | null
  timezone?: string
}

export interface EvidenceCreate {
  case_id: string
  team_id: string
  title: string
  description?: string
  category?: string
  tags?: string[]
  file: File
}

export const evidenceApi = apiSlice.injectEndpoints({
  endpoints: (builder) => ({
    getEvidence: builder.query<Evidence[], { case_id?: string; team_id?: string }>({
      query: (params) => ({
        url: '/evidence/list',
        params,
      }),
      providesTags: ['Evidence'],
    }),
    getEvidenceDeadlineStatus: builder.query<EvidenceDeadlineStatus, string>({
      query: (case_id) => ({
        url: '/evidence/deadline-status',
        params: { case_id },
      }),
    }),
    getEvidencePool: builder.query<EvidencePoolItem[], string>({
      query: (case_id) => ({
        url: '/evidence/pool',
        params: { case_id },
      }),
      providesTags: ['Evidence'],
    }),
    createEvidenceFromPool: builder.mutation<
      Evidence,
      { case_id: string; team_id: string; document_id: string; title?: string }
    >({
      query: (body) => ({
        url: '/evidence/from-pool',
        method: 'POST',
        body,
      }),
      invalidatesTags: ['Evidence'],
    }),
    getEvidenceAiSuggestions: builder.query<
      EvidenceAiSuggestionsResponse,
      { case_id: string; q?: string }
    >({
      query: ({ case_id, q }) => ({
        url: '/evidence/ai-suggestions',
        params: {
          case_id,
          ...(q && q.trim() ? { q: q.trim() } : {}),
        },
      }),
    }),
    getEvidenceById: builder.query<Evidence, string>({
      query: (id) => `/evidence/${id}`,
      providesTags: (result, error, id) => [{ type: 'Evidence', id }],
    }),
    uploadEvidence: builder.mutation<Evidence, FormData>({
      query: (formData) => ({
        url: '/evidence/create',
        method: 'POST',
        body: formData,
      }),
      invalidatesTags: ['Evidence'],
    }),
    deleteEvidence: builder.mutation<{ message: string }, string>({
      query: (id) => ({
        url: `/evidence/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: (result, error, id) => [{ type: 'Evidence', id }],
    }),
    publicizeCaseEvidence: builder.mutation<{ message: string; updated: number }, PublicizeCaseRequest>({
      query: (body) => ({
        url: '/evidence/case/publicize',
        method: 'POST',
        body,
      }),
      invalidatesTags: ['Evidence'],
    }),
    downloadEvidence: builder.query<Blob, string>({
      query: (id) => ({
        url: `/evidence/${id}/download`,
        responseHandler: (response: Response) => response.blob(),
      }),
    }),
  }),
})

export const {
  useGetEvidenceQuery,
  useGetEvidenceDeadlineStatusQuery,
  useGetEvidenceByIdQuery,
  useGetEvidencePoolQuery,
  useCreateEvidenceFromPoolMutation,
  useGetEvidenceAiSuggestionsQuery,
  useLazyGetEvidenceAiSuggestionsQuery,
  useUploadEvidenceMutation,
  useDeleteEvidenceMutation,
  useLazyDownloadEvidenceQuery,
  usePublicizeCaseEvidenceMutation,
} = evidenceApi
